import { ButtonSecondary, Colors, Icon, ModalDialog, Pagination, Text } from '@amelio/ui-library';
import { useEffect, useState } from 'react';

import type { LaunchedSurvey, SurveyStatus } from '@/types/cycle-evaluation/LaunchedSurvey';

import { useExportSurveys, useGetSurveysPaged } from '@/api/cycle-evaluation-services/surveys';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { useError } from '@/hooks/utils/useError';
import { downloadFile } from '@/utils/fileDownload';

type Props = {
    isOpen: boolean;
    sentCycleId: string;
    /** The cycle name: the identity of what people were invited to, never the template name. */
    cycleName: string;
    onClose: () => void;
};

type TranslateFn = ReturnType<typeof useAmelioTranslations>['t'];

type SurveyRowProps = {
    survey: LaunchedSurvey;
    getJobTitle: (jobTitleId: string) => string;
};

const TABLE_COLUMNS = 'grid grid-cols-[1.5fr_1.2fr_1fr_1.8fr_1.2fr] items-center gap-4';
const PAGE_SIZE = 5;

const statusConfig: Record<
    SurveyStatus,
    {
        dotColor: string;
        borderColor: string;
        bgColor: string;
        textColor: string;
        translationKey: string;
    }
> = {
    Completed: {
        dotColor: 'bg-green-500',
        borderColor: 'border-green-300',
        bgColor: 'bg-green-50',
        textColor: 'text-green-700',
        translationKey: 'cycleEvaluationDashboard.launchedSurveys.status.completed',
    },
    InProgress: {
        dotColor: 'bg-yellow-400',
        borderColor: 'border-yellow-300',
        bgColor: 'bg-yellow-50',
        textColor: 'text-yellow-700',
        translationKey: 'cycleEvaluationDashboard.launchedSurveys.status.inProgress',
    },
    NotStarted: {
        dotColor: 'bg-red-400',
        borderColor: 'border-red-300',
        bgColor: 'bg-red-50',
        textColor: 'text-red-600',
        translationKey: 'cycleEvaluationDashboard.launchedSurveys.status.notStarted',
    },
};

type LaunchedSurveysContentProps = {
    isLoading: boolean;
    surveys: LaunchedSurvey[];
    getJobTitle: (jobTitleId: string) => string;
};

const LaunchedSurveysContent = ({
    isLoading,
    surveys,
    getJobTitle,
}: LaunchedSurveysContentProps) => {
    const { t } = useAmelioTranslations();

    if (isLoading) {
        return (
            <Skeleton className={'bg-base-50 flex flex-col gap-2'}>
                {Array.from({ length: 4 }, (_, index) => (
                    <Bone
                        key={index}
                        className={'bg-base h-16 w-full rounded-lg'}
                    />
                ))}
            </Skeleton>
        );
    }

    if (surveys.length === 0) {
        return (
            <div
                className={'py-6 text-center'}
                data-testid={'launched-surveys-empty-state'}
            >
                <Text className={'text-center'}>
                    {t('cycleEvaluationDashboard.noResultsFound')}
                </Text>
            </div>
        );
    }

    return (
        <>
            {surveys.map((survey) => (
                <SurveyRow
                    key={survey.id}
                    survey={survey}
                    getJobTitle={getJobTitle}
                />
            ))}
        </>
    );
};

const SurveyRow = ({ survey, getJobTitle }: SurveyRowProps) => {
    const { t } = useAmelioTranslations();
    const config = statusConfig[survey.status];

    return (
        <div
            className={`${TABLE_COLUMNS} border-base-100 border-b px-4 py-4`}
            data-testid={`launched-survey-row-${survey.id}`}
        >
            <Text
                size={'small'}
                weight={'bold'}
            >
                {survey.employeeName}
            </Text>

            <Text
                size={'small'}
                color={Colors.BASE_600}
            >
                {getJobTitle(survey.role)}
            </Text>

            <Text
                size={'small'}
                className={'text-center'}
            >
                {survey.numberOfReviewers}
            </Text>

            <div className={'flex items-center gap-3'}>
                <div className={'h-2 flex-1 overflow-hidden rounded-full bg-gray-200'}>
                    <div
                        className={'h-full rounded-full bg-purple-600 transition-all'}
                        style={{ width: `${survey.completionRate}%` }}
                    />
                </div>

                <Text
                    size={'small'}
                    className={'w-10 shrink-0 text-right'}
                >
                    {survey.completionRate}%
                </Text>
            </div>

            <div className={'flex justify-end'}>
                <span
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${config.bgColor} ${config.borderColor} ${config.textColor}`}
                >
                    <span className={`h-2 w-2 rounded-full ${config.dotColor}`} />

                    {t(config.translationKey as Parameters<TranslateFn>[0])}
                </span>
            </div>
        </div>
    );
};

export const LaunchedSurveysModal = ({ isOpen, sentCycleId, cycleName, onClose }: Props) => {
    const { t } = useAmelioTranslations();
    const [currentPage, setCurrentPage] = useState(1);
    const [isDownloading, setIsDownloading] = useState(false);

    const { handleError } = useError();

    const { data: surveysData, isLoading } = useGetSurveysPaged({
        sentCycleId,
        pageNumber: currentPage,
        pageSize: PAGE_SIZE,
    });

    const { exportSurveys } = useExportSurveys();
    const { getJobTitle } = useCompanyInformation();

    const surveys = surveysData?.items ?? [];
    const totalCount = surveysData?.totalCount ?? 0;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    useEffect(() => {
        setCurrentPage(1);
    }, [sentCycleId]);

    const handleDownloadExcel = async () => {
        try {
            setIsDownloading(true);
            const blob = await exportSurveys(sentCycleId);
            downloadFile(blob, `surveys-${sentCycleId}-${Date.now()}.xlsx`);
        } catch (error) {
            handleError({ error });
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={onClose}
            title={t('cycleEvaluationDashboard.launchedSurveys.title')}
            titleIcon={
                <Icon
                    name={'security-eye'}
                    color={Colors.SECONDARY_800}
                    ariaHidden
                />
            }
            subtitle={t('cycleEvaluationDashboard.launchedSurveys.subtitle', {
                cycleName,
            })}
            size={'lg'}
            closeAriaLabel={t('common.close')}
            backdropBlur
            dataTestId={'launched-surveys-modal'}
            footer={
                <div className={'flex flex-col gap-3'}>
                    <Text
                        size={'small'}
                        color={Colors.BASE_600}
                        className={'italic'}
                    >
                        {t('cycleEvaluationDashboard.launchedSurveys.footerNote')}
                    </Text>

                    <div className={'flex justify-end'}>
                        <ButtonSecondary
                            size={'md'}
                            dataTestId={'download-excel-button'}
                            onClick={handleDownloadExcel}
                            disabled={isDownloading}
                        >
                            <span className={'flex items-center gap-2'}>
                                <Icon
                                    name={'arrow-arrow-down-1'}
                                    size={16}
                                />

                                {t('cycleEvaluationDashboard.launchedSurveys.downloadExcel')}
                            </span>
                        </ButtonSecondary>
                    </div>
                </div>
            }
        >
            <div
                className={`${TABLE_COLUMNS} bg-base-50 mb-2 rounded-lg px-4 py-3`}
                data-testid={'launched-surveys-table-header'}
            >
                <Text
                    weight={'bold'}
                    size={'small'}
                >
                    {t('cycleEvaluationDashboard.launchedSurveys.columns.evaluatedEmployee')}
                </Text>

                <Text
                    weight={'bold'}
                    size={'small'}
                >
                    {t('cycleEvaluationDashboard.launchedSurveys.columns.role')}
                </Text>

                <Text
                    weight={'bold'}
                    size={'small'}
                    className={'text-center'}
                >
                    {t('cycleEvaluationDashboard.launchedSurveys.columns.numberOfReviewers')}
                </Text>

                <Text
                    weight={'bold'}
                    size={'small'}
                >
                    {t('cycleEvaluationDashboard.launchedSurveys.columns.completionRate')}
                </Text>

                <Text
                    weight={'bold'}
                    size={'small'}
                    className={'text-right'}
                >
                    {t('cycleEvaluationDashboard.launchedSurveys.columns.status')}
                </Text>
            </div>

            <LaunchedSurveysContent
                isLoading={isLoading}
                surveys={surveys}
                getJobTitle={getJobTitle}
            />

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={PAGE_SIZE}
                onPageChange={setCurrentPage}
                isLoading={isLoading}
                dataTestId={'launched-surveys-pagination'}
                resultsSummaryText={t('common.showingResults', {
                    start: totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1,
                    end: Math.min(currentPage * PAGE_SIZE, totalCount),
                    total: totalCount,
                })}
            />
        </ModalDialog>
    );
};
